import React from "react";

import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import { Divider } from "js/Components/Divider";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import {
    navigationSelectedClass,
    StyledNavigationIcon,
    StyledNavigationSideBarItem,
} from "js/editor/PresentationLibrary/libraryNavigationItems";
import { t } from "js/i18n";
import { themeColors } from "js/react/sharedStyles";
import { TeamResourceTypes, TEAM_SETTINGS_PANES } from "js/react/views/TeamResources/TeamResourceTypes";

type LibraryTeamSettingsNavigationProps = {
    alwaysExpanded?: boolean;
    teamPane?: string;
};

type TeamSettingsPermissions = {
    showPlayerSettings: boolean;
    showBrandGuardrails: boolean;
    showTeamFolders: boolean;
};

export const isTeamSettingsPane = (pane?: string): boolean => TEAM_SETTINGS_PANES.includes(pane);

function getDefaultTeamSettingsPane(permissions: TeamSettingsPermissions): string {
    if (permissions.showBrandGuardrails) {
        return TeamResourceTypes.TEAM_BRANDING_GUARDRAILS;
    }
    if (permissions.showPlayerSettings) {
        return TeamResourceTypes.PLAYER_SETTINGS;
    }

    return TeamResourceTypes.TEAM_FOLDERS;
}

function TeamSettingsExpandedMenuItems({ teamPane, permissions }: {
    teamPane?: string;
    permissions: TeamSettingsPermissions;
}) {
    return (
        <>
            {permissions.showBrandGuardrails && (
                <StyledNavigationSideBarItem
                    className={navigationSelectedClass(teamPane === TeamResourceTypes.TEAM_BRANDING_GUARDRAILS)}
                    onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_BRANDING_GUARDRAILS })}
                    data-test-id="sidebar-nav-brand-guardrails"
                >
                    <StyledNavigationIcon>lock</StyledNavigationIcon>
                    {t("Brand Guardrails")}
                </StyledNavigationSideBarItem>
            )}
            {permissions.showPlayerSettings && (
                <StyledNavigationSideBarItem
                    className={navigationSelectedClass(teamPane === TeamResourceTypes.PLAYER_SETTINGS)}
                    onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.PLAYER_SETTINGS })}
                    data-test-id="sidebar-nav-player-settings"
                >
                    <StyledNavigationIcon>play_circle</StyledNavigationIcon>
                    {t("Player Settings")}
                </StyledNavigationSideBarItem>
            )}
            {permissions.showTeamFolders && (
                <>
                    {WorkspaceController.isMultiUserWorkspace && <Divider margin={10} color={themeColors.mediumGray} />}
                    <StyledNavigationSideBarItem
                        className={navigationSelectedClass(teamPane === TeamResourceTypes.TEAM_FOLDERS)}
                        onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_FOLDERS })}
                        data-test-id="sidebar-nav-team-folders"
                    >
                        <StyledNavigationIcon>folder_shared</StyledNavigationIcon>
                        {t("Team Folders")}
                    </StyledNavigationSideBarItem>
                </>
            )}
        </>
    );
}

export function LibraryTeamSettingsNavigation({ teamPane }: LibraryTeamSettingsNavigationProps) {
    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const canManagePlayerSettings = WorkspaceController.actionPermissions[ActionPermissionsObject.PLAYER_SETTINGS].manage;

    const canUpgradeToManagePlayerSettings =
        (canUpgradeToManageSharedResources && canManagePlayerSettings) ||
        (canManageSharedResources && WorkspaceController.canUpgradeToRemoveActionRestriction(
            ActionPermissionsObject.PLAYER_SETTINGS,
            PermissionActionType.MANAGE
        ));
    const canManageBrandGuardrails = WorkspaceController.actionPermissions[ActionPermissionsObject.BRAND_GUARDRAILS].manage;
    const canUpgradeToManageBrandGuardrails = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.BRAND_GUARDRAILS,
        PermissionActionType.MANAGE
    );

    const permissions: TeamSettingsPermissions = {
        showPlayerSettings: canManagePlayerSettings || canUpgradeToManagePlayerSettings,
        showBrandGuardrails: canManageBrandGuardrails || canUpgradeToManageBrandGuardrails,
        showTeamFolders: WorkspaceController.isOwner,
    };

    const hasTeamSettingsNavigation = permissions.showPlayerSettings || permissions.showBrandGuardrails || permissions.showTeamFolders;

    if (!hasTeamSettingsNavigation) {
        return null;
    }

    if (isTeamSettingsPane(teamPane) || !WorkspaceController.isMultiUserWorkspace) {
        return (
            <TeamSettingsExpandedMenuItems
                teamPane={teamPane}
                permissions={permissions}
            />
        );
    }

    return (
        <>
            <Divider margin={5} color="#444" />
            <StyledNavigationSideBarItem
                onClick={() => AppController.showTeamResources({ pane: getDefaultTeamSettingsPane(permissions) })}
                data-test-id="sidebar-nav-team-settings"
            >
                <StyledNavigationIcon>settings</StyledNavigationIcon>
                {t("Team Settings")}
            </StyledNavigationSideBarItem>
        </>
    );
}
